'use client';

import useSWR from 'swr';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import { paths } from 'src/routes/paths';
import { NyttFärgTema } from 'src/theme/NyttFärgtema';
import axiosInstance, { endpoints } from 'src/lib/axios';

import type { KortProps } from '../kort-katalog';
import DomainCard from '../components/domain-card';
import { MiniList, MiniRow, swrOptions } from './kort-primitiver';

// ----------------------------------------------------------------------

interface IVeckansSchema {
    pass?: { id: string; namn?: string; startTid?: string; slutTid?: string; avdelning?: string }[];
}

export default function SchemaKort({ orgId }: KortProps) {
    const { data: schema, isLoading } = useSWR<IVeckansSchema>(
        orgId ? ['schema-vecka', orgId] : null,
        () => axiosInstance.get(endpoints.schema.vecka(orgId)).then((r) => r.data),
        { ...swrOptions, shouldRetryOnError: false }
    );

    const pass = schema?.pass ?? [];

    if (isLoading && !schema) {
        return (
            <DomainCard
                title="Schema & bemanning"
                icon="solar:calendar-bold"
                theme={NyttFärgTema.Kombinationer[1]}
                href={paths.dashboard.förskola.schema}
                primaryMetric={{ value: '—', label: 'pass denna vecka' }}
            >
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    if (pass.length === 0) {
        return (
            <DomainCard
                title="Schema & bemanning"
                icon="solar:calendar-bold"
                theme={NyttFärgTema.Kombinationer[1]}
                href={paths.dashboard.förskola.schema}
                emptyMessage="Skapa ett veckoschema för att planera personalens pass och bemanningsnivåer."
            />
        );
    }

    return (
        <DomainCard
            title="Schema & bemanning"
            icon="solar:calendar-bold"
            theme={NyttFärgTema.Kombinationer[1]}
            href={paths.dashboard.förskola.schema}
            primaryMetric={{ value: pass.length, label: pass.length === 1 ? 'pass denna vecka' : 'pass denna vecka' }}
        >
            <MiniList>
                {pass.slice(0, 3).map((p) => (
                    <MiniRow
                        key={p.id}
                        primary={p.namn || 'Pass'}
                        secondary={p.avdelning}
                        trailing={p.startTid && p.slutTid ? `${p.startTid}–${p.slutTid}` : undefined}
                    />
                ))}
            </MiniList>
        </DomainCard>
    );
}
